/**
 * _composeHelpers.ts — module consts, PersistedDraft, and pure
 * address/HTML helpers extracted from ComposePage.tsx (zero behaviour change).
 */
import {
  EMAIL_SIGNATURE_CACHE_KEY,
  saveSignatureHtml,
  fetchDurableSignatureHtml,
  resolveSignatureForAccount,
  resolveSignatureWithDefault,
  buildDefaultSignatureHtml,
  saveSignatureForAccount,
  loadSignatureForScope,
  scopeIsOptedOut,
  NO_SIGNATURE_SENTINEL,
  fetchDurableSignaturesByAccount,
} from '@quotech/tenant-shared/settings/emailSignature'
import type { SignatureIdentity } from '@quotech/tenant-shared/settings/emailSignature'

// Per-account (per-mailbox) signature overrides — thin re-exports so the
// composer + signature editor import from one place (mirrors the default
// signature shims above). See FEAT-2026-06-18-EMAIL-PER-ACCOUNT-SIGNATURE.
// `resolveSignatureWithDefault` / `buildDefaultSignatureHtml` add the
// Quotech-branded fallback (foreman beaver) so EVERY compose carries a
// signature even before the operator saves their own.
export {
  resolveSignatureForAccount,
  resolveSignatureWithDefault,
  buildDefaultSignatureHtml,
  saveSignatureForAccount,
  loadSignatureForScope,
  scopeIsOptedOut,
  NO_SIGNATURE_SENTINEL,
  fetchDurableSignaturesByAccount,
}
export type { SignatureIdentity }

// The signature now lives in ONE canonical store
// (@quotech/tenant-shared/settings/emailSignature) shared with every
// Settings surface, so a signature "set up" in Settings actually reaches
// the send path (it used to write a dead `email_preferences.signature`
// field nothing read). These three names are kept as thin re-export
// shims so the composer's existing imports (useComposePage,
// SignatureEditorModal) are unchanged. The cache key + view-pref scope
// are byte-identical to before, so existing operators keep their saved
// signatures. See FEAT-2026-06-18-EMAIL-SIGNATURE-UNIFY.
export const SIGNATURE_STORAGE_KEY = EMAIL_SIGNATURE_CACHE_KEY

/** Persist a signature write to BOTH the instant localStorage cache and
 *  the durable per-user/per-org store. Pass '' to clear it. */
export function persistSignatureToDb(html: string): void {
  saveSignatureHtml(html)
}

/** One-shot pull of the durable signature. Writes it into the
 *  localStorage cache so synchronous reads pick it up, and returns it.
 *  Returns null when the operator has none on the server / is offline. */
export async function hydrateSignatureFromDb(): Promise<string | null> {
  return fetchDurableSignatureHtml()
}

export const AUTOSAVE_INTERVAL_MS = 5_000

export interface PersistedDraft {
  to: string[]
  cc: string[]
  bcc: string[]
  subject: string
  body_html: string
  /** Reply / forward / reply_all marker so resuming hops back to
   * the right URL flavour. We don't include the original email's
   * id — if it's gone (deleted from inbox) the draft simply
   * resumes as a free-standing new message.  */
  intent?: 'new' | 'reply' | 'forward' | 'reply_all'
  intent_email_id?: string | null
  /** Per-window id for a `new` draft so several independent new
   *  messages can be kept at once and resumed to their exact slot.
   *  Undefined for reply/forward (those key off intent_email_id). */
  draft_id?: string | null
  /** Send-as mailbox + outbound priority. BUG-2026-06-23: these were
   *  server-draft-only, so picking a non-default From or High/Low priority
   *  and reopening/resuming before the server sync landed (or while offline)
   *  reverted them to the defaults. Persisted locally now so the instant
   *  cache restores them too. */
  from_account_id?: number | null
  importance?: 'low' | 'normal' | 'high'
  saved_at: number
}

export function csvToList(value: string): string[] {
  return value
    .split(/[,;]/)
    .map(e => e.trim())
    .filter(Boolean)
}

/** Strip MIME / HTML / quoted-printable noise out of an email
 * address so "Mark Newman <mark@…>" → "mark@…". */

export function bareAddress(input: string | null | undefined): string {
  if (!input) return ''
  const m = input.match(/<([^>]+)>/)
  return (m ? m[1] : input).trim().toLowerCase()
}

/**
 * ARC-1 — union of the card ids embedded in the body (card chips) and
 * the host-supplied extras (`extraLinkedCardIds`, e.g. the card the
 * inline drawer composer belongs to). Pure I→O, order-preserving,
 * deduped, empty strings dropped — the send payload's
 * `linked_card_ids` must never carry duplicates or blanks.
 */
export function mergeLinkedCardIds(
  fromBody: string[],
  extra?: string[] | null,
): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const id of [...fromBody, ...(extra ?? [])]) {
    const norm = (id || '').trim()
    if (!norm || seen.has(norm)) continue
    seen.add(norm)
    out.push(norm)
  }
  return out
}

export function dedupeAddresses(addrs: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const a of addrs) {
    const norm = bareAddress(a)
    if (!norm || seen.has(norm)) continue
    seen.add(norm)
    out.push(a)
  }
  return out
}

/** Convert sanitized HTML into a plain-text fallback (used as the
 * `body` field on the wire — the BE prefers `body_html` but uses
 * `body` for the SMTP plain alternative + the in-app preview). */

export function htmlToPlainText(html: string): string {
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  return (tmp.innerText || tmp.textContent || '').trim()
}

/**
 * ComposePage works in two modes:
 *   - "page" mode: rendered as the `/email/compose` route (default).
 *     Reads reply/forward intent from URL search params, navigates
 *     to `/email/inbox` on send + discard.
 *   - "embed" mode: rendered inside the floating ComposeDock. Reads
 *     intent from props supplied by the dock store; onSent /
 *     onClose callbacks replace the inbox navigation so closing the
 *     dock doesn't yank the operator off whatever email-hub page
 *     they're currently viewing (the whole reason the dock exists).
 *
 * Optional-props approach instead of an extract-to-shared-form
 * refactor: v2 ComposePage already has deep effect coupling (resume,
 * prefill, autosave, AI draft, signature, paste hand-off); lifting
 * all of that into a new component is a multi-file surgery with
 * high regression risk in one MR. Optional props give the dock
 * everything it needs without touching the v2 internals.
 */
